import { useMemo } from "react"

import type { DashboardUseCase } from "../../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { formatNumber, formatUSD } from "../../../utils/format-helpers"

type SiteCardProps = {
  site: string
  useCases: DashboardUseCase[]
  color: string
}

export function SiteCard({ site, useCases, color }: SiteCardProps) {
  const kpis = useMemo(() => {
    let benefit = 0
    let roi = 0
    for (const uc of useCases) {
      benefit += uc.annualBenefit
      roi += uc.roi12m
    }
    return { count: useCases.length, benefit, roi }
  }, [useCases])

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <div
            className="border-foreground size-3 rounded-full border"
            style={{ backgroundColor: color }}
          />
          {site}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-muted-foreground text-xs">Count</p>
            <p className="text-lg font-bold">{formatNumber(kpis.count)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Benefit</p>
            <p className="text-lg font-bold">{formatUSD(kpis.benefit)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">ROI</p>
            <p
              className={`text-lg font-bold ${kpis.roi >= 0 ? "text-success" : "text-destructive"}`}
            >
              {formatUSD(kpis.roi)}
            </p>
          </div>
        </div>

        {useCases.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-2 py-1 text-xs">Use Case</TableHead>
                <TableHead className="px-2 py-1 text-right text-xs">Benefit</TableHead>
                <TableHead className="px-2 py-1 text-right text-xs">ROI</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {useCases.slice(0, 10).map((uc) => (
                <TableRow key={uc.id}>
                  <TableCell className="max-w-[150px] truncate px-2 py-1 text-xs">
                    {uc.ucName}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-right text-xs tabular-nums">
                    {formatUSD(uc.annualBenefit)}
                  </TableCell>
                  <TableCell
                    className={`px-2 py-1 text-right text-xs tabular-nums ${uc.roi12m >= 0 ? "text-success" : "text-destructive"}`}
                  >
                    {formatUSD(uc.roi12m)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
